import api from './api';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface Apontamento {
  id: string;
  ticketId: string;
  userId: string;
  data: string;
  horaInicio: string;
  horaFim: string;
  minutosTotais: number;
  tipoHora: 'HTC' | 'HTE' | 'PROJETO';
  valorHora: string;
  descricao: string;
  faturado: boolean;
  faturaId: string | null;
  createdAt: string;
  ticket: {
    id: string;
    codigo: string;
    titulo: string;
    client?: {
      id: string;
      razaoSocial: string;
      nomeFantasia: string | null;
    };
  };
  user: {
    id: string;
    name: string;
  };
  anexos?: Array<{
    id: string;
    filename: string;
    mimetype: string;
    size: number;
    createdAt: string;
  }>;
  fatura?: {
    id: string;
    numero: number;
    status: string;
  } | null;
}

export interface ApontamentoCreateData {
  ticketId: string;
  userId?: string;
  data: string; // ISO date string
  horaInicio: string; // ISO datetime string
  horaFim: string; // ISO datetime string
  tipoHora: 'HTC' | 'HTE' | 'PROJETO';
  descricao: string;
}

export interface ApontamentoUpdateData {
  data?: string;
  horaInicio?: string;
  horaFim?: string;
  tipoHora?: 'HTC' | 'HTE' | 'PROJETO';
  descricao?: string;
}

export interface ApontamentoFilters {
  ticketId?: string;
  clientId?: string;
  userId?: string;
  faturado?: boolean;
  dataInicio?: string;
  dataFim?: string;
  tipoHora?: 'HTC' | 'HTE' | 'PROJETO';
}

export interface ValoresHora {
  HTC: number;
  HTE: number;
  PROJETO: number;
}

// ─── Service Functions ────────────────────────────────────────────────────────

/**
 * Busca os valores de hora por tipo (fonte única de verdade no backend)
 */
export const getValoresHora = async (): Promise<ValoresHora> => {
  const response = await api.get('/apontamentos/valores-hora');
  return response.data;
};

/**
 * Cria um novo apontamento
 */
export const createApontamento = async (data: ApontamentoCreateData): Promise<Apontamento> => {
  const response = await api.post('/apontamentos', data);
  return response.data;
};

/**
 * Lista apontamentos com filtros opcionais
 */
export const listApontamentos = async (filters?: ApontamentoFilters): Promise<Apontamento[]> => {
  const params = new URLSearchParams();
  
  if (filters?.ticketId) params.append('ticketId', filters.ticketId);
  if (filters?.clientId) params.append('clientId', filters.clientId);
  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.faturado !== undefined) params.append('faturado', String(filters.faturado));
  if (filters?.tipoHora) params.append('tipoHora', filters.tipoHora);
  if (filters?.dataInicio) params.append('dataInicio', filters.dataInicio);
  if (filters?.dataFim) params.append('dataFim', filters.dataFim);

  const response = await api.get(`/apontamentos?${params.toString()}`);
  return response.data;
};

/**
 * Busca apontamento por ID
 */
export const getApontamentoById = async (id: string): Promise<Apontamento> => {
  const response = await api.get(`/apontamentos/${id}`);
  return response.data;
};

/**
 * Atualiza um apontamento
 */
export const updateApontamento = async (id: string, data: ApontamentoUpdateData): Promise<Apontamento> => {
  const response = await api.put(`/apontamentos/${id}`, data);
  return response.data;
};

/**
 * Remove um apontamento
 */
export const deleteApontamento = async (id: string): Promise<void> => {
  await api.delete(`/apontamentos/${id}`);
};
